// Finding — a structured observation inside a report (a damage, a missing
// item, an incident, a non-conformity). Generated by AI and editable by
// the inspector.
//
// `aiDescription` is the original AI-generated text and is immutable —
// preserving it is what lets us measure how often the inspector overrode
// the model. v2 calls this the "ontología correctiva propietaria"; we keep
// the same invariant.
import { z } from "zod";
import { isoDateTimeSchema, slugSchema } from "@bitacorapro/shared";
import { assetIdSchema, findingIdSchema, organizationIdSchema, reportIdSchema } from "../ids.js";
export const findingSchema = z.object({
    id: findingIdSchema,
    // ADR-0065 — every finding belongs to an asset; reportId (below) is
    // provenance ("was this created inside an acta's capture flow"), not
    // ownership. Both are denormalized from the report when one exists;
    // assetId is the sole anchor when it doesn't (a loose observation).
    assetId: assetIdSchema,
    organizationId: organizationIdSchema,
    // Nullable (ADR-0065, was required) — undefined/absent for a "loose
    // observation" created directly against the asset after its milestone
    // acta is signed (POST /assets/:id/observations). Present for a
    // finding born inside a report's capture flow, same as always.
    reportId: reportIdSchema.optional(),
    // The recipe defines the area pool; areaName is one of those names. Order
    // is how findings are presented in the PDF.
    areaName: z.string().trim().min(1).max(100),
    areaOrder: z.number().int().nonnegative(),
    // The element that has the issue (e.g. "muro norte", "lavamanos"). Free
    // text — there's no closed list because this is what the AI fills in.
    element: z.string().trim().max(255).optional(),
    aiDescription: z.string().trim().max(5_000).optional(),
    // ADR-0117 — short semantic headline (3–6 words) the model writes in
    // the same analysis call as aiDescription. Immutable AI baseline like
    // aiDescription: declared here so every vertical carries it, absent
    // from findingUpdateSchema below on purpose. Undefined when the
    // creation path made no analysis call or the row predates the column;
    // readers fall back to element / a clamped description.
    aiTitle: z.string().trim().max(80).optional(),
    humanDescription: z.string().trim().max(5_000).optional(),
    // category and severity validated against recipe in invariants.
    category: slugSchema,
    severity: slugSchema,
    aiGenerated: z.boolean(),
    audioTranscript: z.string().max(20_000).optional(),
    createdAt: isoDateTimeSchema,
    // ADR-0067 — when the observation was made, when that differs from
    // when the row was written. Undefined/null means the two coincide
    // (every live-capture path, every loose ADR-0065 observation).
    // Non-null only for checklist-materialized findings (ADR-0066),
    // stamped from the parent report's own timestamp. Unlike createdAt
    // (server clock, never caller-settable — omitted below), this stays
    // in findingCreateSchema on purpose.
    observedAt: isoDateTimeSchema.optional(),
});
export const findingCreateSchema = findingSchema
    .omit({ id: true, createdAt: true })
    .extend({ id: findingIdSchema.optional() });
// Inspectors edit element / humanDescription / category / severity. The
// aiDescription and aiTitle fields are intentionally NOT editable —
// they're the immutable AI baseline.
export const findingUpdateSchema = findingSchema
    .pick({
    element: true,
    humanDescription: true,
    category: true,
    severity: true,
})
    .partial();
//# sourceMappingURL=finding.js.map